import {
    CognitoUserPool,
    CognitoUser,
    CognitoUserSession,
    CognitoIdToken,
    CognitoAccessToken,
    CognitoRefreshToken
} from 'amazon-cognito-identity-js';
import {Config, CognitoIdentityCredentials} from 'aws-sdk';
import axios from 'axios';

/** @class */
export default class Cognito {
    static install = (Vue, options) => {
        Object.defineProperty(Vue.prototype, '$cognito', {
            get() {
                if (!this.$root._cognito) {
                    this.$root._cognito = new Cognito(options);
                }
                return this.$root._cognito;
            }
        });

        Vue.mixin({
            beforeCreate() {
                if (!this.$root._cognito) {
                    this.$root._cognito = new Cognito(options);
                }
            }
        });
    };

    /**
     * @constructor
     * @param config
     */
    constructor(config) {
        Config.region = config.COGNITO.REGION;
        Config.credentials = new CognitoIdentityCredentials({
            IdentityPoolId: config.COGNITO.IDENTITYPOOLID
        });
        this.userPool = new CognitoUserPool({
            UserPoolId: config.COGNITO.USERPOOLID,
            ClientId: config.COGNITO.USERPOOLCLIENTID
        });
        this.clientId_ = config.COGNITO.USERPOOLCLIENTID;
        this.oAuthBaseUrl_ = config.COGNITO.OAUTH_BASE_URL;
        this.cognitoUser_ = null;

        return this;
    }

    /**
     *
     * @param redirectPath
     */
    oAuthForGoogle(redirectPath) {
        // window.location.hrefと同じ。
        // Method 呼び出しで実装。
        let redirectUri = this.getRedirectUri_(redirectPath);
        // getUserAttributes を Call するために、scope === aws.cognito.signin.user.adminが必要。
        let authUrl = `${this.oAuthBaseUrl_}/login?response_type=code` +
            `&client_id=${this.clientId_}&scope=email+openid+aws.cognito.signin.user.admin&redirect_uri=${redirectUri}`;
        window.location.assign(
            authUrl
        )
    }


    /**
     * ログインしているユーザーのAuthTokenを取得。
     */
    async authToken(redirectPath = Cognito.BASE_REDIRECT_PATH_) {
        let self_ = this;
        let code = this.getAuthInfo_()['code'];
        if (!code) {
            this.oAuthForGoogle(redirectPath);
            return;
        }
        let session = await self_.getSession_().catch(async function (err) {
            await self_.authenticateCognito_(code, redirectPath).catch(function (err) {
                self_.handleAuthError_(err, redirectPath);
            });
            return await self_.getSession_();
        });
        if (session) {
            return session.getIdToken().getJwtToken();
        }
        return await self_.authenticateCognito_(code, redirectPath).catch(function (err) {
            self_.handleAuthError_(err, redirectPath);
        });

    }

    /**
     *
     * @param code {string} oAuth後に発行されるコード
     * @param redirectPath {string}
     * @returns {Promise<string>}
     */
    async authenticateCognito_(code, redirectPath) {
        let self_ = this;
        const params = new URLSearchParams();
        params.append("grant_type", "authorization_code");
        params.append("code", code);
        params.append("redirect_uri", self_.getRedirectUri_(redirectPath));
        params.append("client_id", this.clientId_);
        let token = await axios.post(
            `${this.oAuthBaseUrl_}/oauth2/token`,
            params,
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            }
        ).catch(function (err) {
            self_.handleAuthError_(err, redirectPath);
        });
        let userInfo = await axios.post(`${this.oAuthBaseUrl_}/oauth2/userInfo`, {}, {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Authorization": `Bearer ${token.data['access_token']}`
            }
        }).catch(function (err) {
            self_.handleAuthError_(err, redirectPath);
        });
        const userData = {Username: userInfo.data.username, Pool: this.userPool};
        this.cognitoUser_ = new CognitoUser(userData);
        const cognitoSession = new CognitoUserSession({
            IdToken: new CognitoIdToken({IdToken: token.data['id_token']}),
            AccessToken: new CognitoAccessToken({AccessToken: token.data['access_token']}),
            RefreshToken: new CognitoRefreshToken({RefreshToken: token.data['refreshToken']})
        });
        this.cognitoUser_.setSignInUserSession(cognitoSession);

        let session = await this.getSession_();
        if (!session) {
            this.handleAuthError_('Session Is Expired..', redirectPath);
        }
        return session.getIdToken().getJwtToken();
    }

    getSession_() {
        let self_ = this;
        return new Promise((resolve, reject) => {
            let currentUser = self_.getCurrentUser_();
            if (currentUser === null) {
                resolve(null);
                return;
            }
            currentUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                    return;
                }
                return resolve(session);
            });
        });
    }

    getCurrentUser_() {
        return this.userPool.getCurrentUser();
    }


    /**
     * 認証関連で失敗した時に、エラーハンドリングを行う。
     * @param err
     */
    handleAuthError_(err, redirectPath) {
        console.log(err);
        // ここは、内部の人しか使わない。めったに発生しない想定なので、アラートでOK.
        this.oAuthForGoogle(redirectPath);
    }

    getRedirectUri_(redirectPath) {
        return `${location.protocol}//${location.host}/${redirectPath}`;
    }

    setLoginCode(code = '') {
        if (!code) {
            return;
        }
        let authInfo = this.getAuthInfo_();
        authInfo['code'] = code;
        this.setAuthInfo_(authInfo);
    }


    getAuthInfo_() {
        try {
            let storageStr = localStorage.getItem(Cognito.LOCAL_STORAGE_KEY_);
            if (storageStr) {
                return JSON.parse(storageStr);
            }
            return {};
        } catch (e) {
            //　握りつぶす。
            return {};
        }

    };


    setAuthInfo_(authInfo) {
        try {
            localStorage.setItem(Cognito.LOCAL_STORAGE_KEY_, JSON.stringify(authInfo));
        } catch (e) {
            //　握りつぶす。
        }

    };

    getUserAttributes() {
        let self_ = this;
        return new Promise((resolve) => {
            let user = self_.getCurrentUser_();
            if (!user) {
                resolve([]);
            }
            user.getSession((err, session) => {
                user.getUserAttributes((err, result) => {
                    if (err) {
                        console.log(err);
                        resolve([])
                    }
                    resolve(result)
                });
            });
        });
    }


    getUserAttribute(attributeName) {
        let self_ = this;
        return new Promise((resolve) => {
            self_.getUserAttributes().then(
                function (attributes) {
                    let attributeValue = '';
                    attributes.some(function (attribute) {
                        if (attribute.getName() === attributeName) {
                            attributeValue = attribute.getValue();
                        }
                    });
                    return resolve(attributeValue);
                }
            );
        });
    }


    getUserEmail() {
        let self_ = this;
        return new Promise((resolve) => {
            self_.getUserAttribute('email').then(function (res) {
                return resolve(res);
            })
        });
    }


    static get BASE_REDIRECT_PATH_() {
        return 'sales_force';
    }


    static get LOCAL_STORAGE_KEY_() {
        return 'rehaplan.contract_application.auth'

    }
}



// WEBPACK FOOTER //
// ./src/custom_plugins/auth/cognito.js